import { useLazyQuery } from "@apollo/client";
import { Button, DialogActions, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { DateTime } from "luxon";
import { ReactElement, useEffect, useState } from "react";
import * as Yup from "yup";

import {
  ActionKitIntegration,
  CreateActionKitIntegrationInput,
  GetLatestActionKitSubmissionQuery,
  GetLatestActionKitSubmissionQueryVariables,
  UpdateActionKitIntegrationInput,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink } from "~frontend/components/Link";
import ReadOnlyClickToCopyList from "~frontend/components/ReadOnlyClickToCopyList";
import { FormikSwitchField } from "~frontend/components/forms";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import CheckCircle from "~frontend/components/icons/CheckCircle";
import {
  CREATE_ACTION_KIT_INTEGRATION,
  GET_ACTION_KIT_WEBHOOK_URL,
  GET_LATEST_ACTION_KIT_SUBMISSION,
  UPDATE_ACTION_KIT_INTEGRATION,
} from "~frontend/graphql/integration";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { AfterClose } from ".";

const creationValidationSchema = Yup.object({
  name: Yup.string().required("Required"),
  username: Yup.string().required("Required"),
  password: Yup.string().required("Required"),
  domain: Yup.string()
    .required("Required")
    .matches(/^((?!(^https?:\/\/)).)*$/i, {
      message: "Don't include http prefix",
    }),
});

const updateValidationSchema = Yup.object({
  name: Yup.string().required("Required"),
  username: Yup.string().required("Required"),
  password: Yup.string(),
  domain: Yup.string()
    .required("Required")
    .matches(/^((?!(^https?:\/\/)).)*$/i, {
      message: "Don't include http prefix",
    }),
});

function WebhookUrlViewer({
  integration,
  webhookUrl,
  onClick,
}: {
  integration: ActionKitIntegration | null;
  webhookUrl: string | null;
  onClick: () => void;
}): ReactElement {
  if (!integration) {
    return <div />;
  }

  return (
    <FlexBox justifyContent="space-between" sx={{ py: 2 }}>
      {webhookUrl ? (
        <ReadOnlyClickToCopyList
          listData={[{ label: "Endpoint URL", value: webhookUrl }]}
        />
      ) : (
        <>
          <Typography>
            The URL for the webhook contains login credentials and should be
            treated as a secret.
          </Typography>
          <Button onClick={onClick}>View Webhook URL</Button>
        </>
      )}
    </FlexBox>
  );
}

export function ActionKitIntegrationSettingsForm({
  integration,
  subaccountId,
  afterCreate,
  onClose,
}: {
  integration: ActionKitIntegration | null;
  subaccountId: string;
  afterCreate?: () => void;
  onClose: (afterClose?: AfterClose) => void;
}): ReactElement {
  const editing = !!integration;
  const subaccount = useSubaccountAdminConfig()!;

  const shortcodeEnabled =
    !!subaccount.shortcode && subaccount.shortcode.state === "ACTIVE";
  const webhooksEnabled = useFeatureFlag("actionKitWebhooks");

  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);

  const [loadLatestActionKitSubmission, { called, data }] = useLazyQuery<
    GetLatestActionKitSubmissionQuery,
    GetLatestActionKitSubmissionQueryVariables
  >(GET_LATEST_ACTION_KIT_SUBMISSION);

  useEffect(() => {
    if (integration && integration.id) {
      void loadLatestActionKitSubmission({
        variables: {
          input: {
            integrationId: integration.id,
          },
        },
      });
    }
  }, [integration, loadLatestActionKitSubmission]);

  const [createActionKitIntegration] = useMutation(
    CREATE_ACTION_KIT_INTEGRATION,
    {
      refetchQueries: ["ListIntegrations"],
      awaitRefetchQueries: true,
    }
  );

  const [updateActionKitIntegration] = useMutation(
    UPDATE_ACTION_KIT_INTEGRATION,
    {
      refetchQueries: ["ListIntegrations"],
      awaitRefetchQueries: true,
    }
  );

  const [getWebhookUrl] = useMutation(GET_ACTION_KIT_WEBHOOK_URL, {});

  async function onViewWebhookUrl() {
    if (!integration) {
      setWebhookUrl(null);
      return;
    }

    const { errors, data } = await getWebhookUrl({
      variables: { input: { integrationId: integration.id } },
    });

    if (errors) {
      throw errors;
    }
    setWebhookUrl(data.actionKitIntegrationGetWebhookUrl.webhookUrl);
  }

  async function createIntegration(values: CreateActionKitIntegrationInput) {
    const { errors, data } = await createActionKitIntegration({
      variables: { input: values },
    });

    if (errors) {
      throw errors;
    }
    if (afterCreate) {
      afterCreate();
    }
    onClose({
      action: "edit",
      integration: data.createActionKitIntegration as ActionKitIntegration,
    });
  }

  async function updateIntegration(values: UpdateActionKitIntegrationInput) {
    const { errors } = await updateActionKitIntegration({
      variables: { input: values },
    });

    if (errors) {
      throw errors;
    }

    onClose();
  }

  const getErrorMessage = useErrorMessageFormatter();
  const label = editing ? "Password (leave blank to keep current)" : "Password";
  const initialValues = {
    name: integration?.name || "",
    username: integration?.settings?.username || "",
    password: "",
    domain: integration?.settings?.domain || "",
    subscribeSubmissions: !!integration?.settings?.subscribeSubmissions,
  };

  function formatTimeStamp(timestamp: number) {
    const dt = DateTime.fromMillis(timestamp);
    const t = dt
      .toLocaleString({
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      })
      .toLocaleLowerCase()
      .replace(" ", "");
    const d = dt.toLocaleString({
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return `${t} on ${d}`;
  }

  return (
    <Formik
      initialValues={{
        [submitErrorField]: null,
        ...initialValues,
      }}
      validationSchema={
        editing ? updateValidationSchema : creationValidationSchema
      }
      onSubmit={async (values, formik) => {
        const { name, username, domain, subscribeSubmissions } = values;

        const input = {
          name,
          username,
          domain,
          subscribeSubmissions,
        };

        try {
          if (editing) {
            await updateIntegration({
              ...input,
              ...(values.password ? { password: values.password } : {}),
              integrationId: integration.id,
            });
          } else {
            await createIntegration({
              ...input,
              password: values.password,
              subaccountId,
            });
          }

          formik.resetForm();
        } catch (e) {
          formik.setErrors({
            [submitErrorField]: getErrorMessage(e),
          });
        }
      }}
    >
      {({ isSubmitting, values }) => {
        return (
          <Form>
            <Typography variant="body1" sx={{ pb: 2 }}>
              After adding your ActionKit integration, you’ll be able to load
              contact lists into your texting campaigns by providing the
              ActionKit Report ID you’d like to target.
            </Typography>
            {shortcodeEnabled && webhooksEnabled ? (
              <Typography variant="body1" sx={{ pb: 2 }}>
                You can also sync opt ins collected on ActionKit forms, so that
                they are included in your short code subscriber list. To do this
                you’ll have to configure the webhook generated from this
                integration with your ActionKit account.
              </Typography>
            ) : null}
            <FormikErrorBanner />
            <FormikTextField
              name="name"
              label="Name Your Integration"
              disabled={isSubmitting}
              fullWidth
            />
            <FormikTextField
              name="username"
              label="Username"
              disabled={isSubmitting}
              fullWidth
            />
            <FormikTextField
              label={label}
              name="password"
              type="password"
              disabled={isSubmitting}
              fullWidth
            />
            <FormikTextField
              label="Domain"
              name="domain"
              disabled={isSubmitting}
              fullWidth
              placeholder="roboticdogs.actionkit.com"
            />
            {shortcodeEnabled && webhooksEnabled ? (
              <>
                <FormikSwitchField
                  name="subscribeSubmissions"
                  label="Subscribe form submissions to your short code?"
                />
                {values.subscribeSubmissions ? (
                  <>
                    <Typography variant="body1" sx={{ pt: 2, pb: 1 }}>
                      To complete your short code subscriber sync, you’ll need
                      to create a new endpoint in your ActionKit account, using
                      the webhook URL provided here. Once completed, you can
                      return to this page to confirm that your sync is complete.{" "}
                      <ExternalLink
                        href="https://scaletowin.freshdesk.com/support/solutions/articles/66000511714-action-kit-integration"
                        sx={{ color: palette.navy }}
                      >
                        Read more about how to create an endpoint in ActionKit
                        here.
                      </ExternalLink>
                    </Typography>
                    {integration ? (
                      <>
                        <WebhookUrlViewer
                          integration={integration}
                          webhookUrl={webhookUrl}
                          onClick={onViewWebhookUrl}
                        />
                        {integration.metadata &&
                        integration.metadata.webhookConnectedAt ? (
                          <FlexBox
                            sx={{
                              backgroundColor: palette.lightGreen,
                              p: "10px",
                              borderRadius: "4px",
                            }}
                            gap={1}
                            alignItems="flex-start"
                          >
                            <CheckCircle
                              color="success"
                              sx={{ width: "18px" }}
                            />
                            <FlexBox direction="column" sx={{ pt: "1px" }}>
                              <Typography variant="body2Bold">
                                Connected to ActionKit:{" "}
                                <Typography component={"span"} variant="body2">
                                  {formatTimeStamp(
                                    integration.metadata.webhookConnectedAt
                                  )}
                                </Typography>
                              </Typography>
                              <Typography variant="body2Bold">
                                Last form received:{" "}
                                <Typography component={"span"} variant="body2">
                                  {called &&
                                  data &&
                                  data.getLatestActionKitSubmission
                                    ? `"${data.getLatestActionKitSubmission.pageName}" on ${formatTimeStamp(
                                        data.getLatestActionKitSubmission
                                          ?.updatedAt
                                      )}`
                                    : "--"}
                                </Typography>
                              </Typography>
                            </FlexBox>
                          </FlexBox>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <FlexBox
                          alignItems="center"
                          justifyContent="center"
                          sx={{ p: 3 }}
                        >
                          <Typography sx={{ fontStyle: "italic" }}>
                            Save this integration to generate an endpoint URL.
                          </Typography>
                        </FlexBox>
                      </>
                    )}
                  </>
                ) : null}
              </>
            ) : null}

            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={() => onClose()}
                color="secondary"
              >
                Cancel
              </Button>
              <FormikSubmitButton
                color="primary"
                variant="contained"
                submitText={editing ? "Save" : "Create"}
                loadingText={editing ? "Saving..." : "Creating..."}
              />
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
}
